import { Component, ViewChild, OnInit } from '@angular/core';
import { FormControl,FormGroup,Validators, FormBuilder } from '@angular/forms';
import { PharmacyStoreSearchRequestModel, PharmacyStoreSearchResponseModel, PharmacyStoreSearchGridData } from '../../DTO/pharmacy-store-search';
import { Observable, map, startWith, Subject, of, fromEvent } from 'rxjs';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { PharmacyService } from 'src/app/services/pharmacyservice';
import { ToastrService } from 'ngx-toastr';
import * as XLSX from 'xlsx';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-pharmacy-store-search',
  templateUrl: './pharmacy-store-search.component.html',
  styleUrls: ['./pharmacy-store-search.component.css']
})
export class PharmacyStoreSearchComponent implements OnInit {
  form!: FormGroup;
  PharmacyChain: FormControl;
  NCPDPID: FormControl;
  dataSource: any[] = [];
  chainList:any;
  displayedColumns: any[] = ['NCPDPID', 'NPI','PharmacyName','PID','IsEnabled', 'RecordActiveDate','RecordDeactivateDate','UpdatedDate','UpdatedBy'];
  pharmacyStoreGridData=new PharmacyStoreSearchGridData();
  searchInput$ = new Subject<string>();
  pharmacyStoreGridCopy:any;
  pharmacyStoreSearchRequestModel = new PharmacyStoreSearchRequestModel();
  filteredOptions: Observable<any> | undefined;
  panelOpenState = false;
  pageSizeOptions: number[] = [10, 20, 30, 100];
  pharmacyPagesize = 10;
  pharmacyPageNumber = 1;
  length: number = 0;
  isLoading: boolean = false;
  currentChainPage = 1;
  hasMoreChains = true;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  ngOnInit(): void {
    this.createFormControls();
    this.createForm();

    // Load initial 500 chains
    this.LoadPharmacyChains('', 1, 500);

    this.PharmacyChain.valueChanges.pipe(
      debounceTime(300),
      tap(() => {
        this.isLoading = true;
        this.currentChainPage = 1; // reset page
      }),
      switchMap(value => {
        const term = typeof value === 'string' ? value : value?.PharmacyName || '';
        return this.pharmacyservice.getActivePharmacyChains(term, this.currentChainPage, 50);
      })
    ).subscribe((res: any) => {
      this.chainList = res?.ResponseData?.ResponseData ?? [];
      this.filteredOptions = of(this.chainList);
      this.isLoading = false;
    });

  }

    constructor(private pharmacyservice: PharmacyService, private toastr:ToastrService) {

  }

  ngAfterViewInit(): void {
    this.autocompleteTrigger.panelClosingActions
      .pipe(
        tap(() => {
          // Wait for panel to open
          setTimeout(() => {
            const panel = document.querySelector('.mat-autocomplete-panel');
            if (panel) {
              fromEvent(panel, 'scroll')
                .pipe(debounceTime(100))
                .subscribe(() => {
                  const threshold = 150;
                  const position = panel.scrollTop + panel.clientHeight;
                  const height = panel.scrollHeight;

                  if (height - position < threshold && !this.isLoading) {
                    this.loadMorePharmacyChains();
                  }
                });
            }
          }, 0); // Delay to ensure panel is rendered
        })
      )
      .subscribe();
  }

    loadMorePharmacyChains() {
      if (!this.hasMoreChains) return;

      this.isLoading = true;
      this.currentChainPage++;

      const term = typeof this.PharmacyChain.value === 'string'
        ? this.PharmacyChain.value
        : this.PharmacyChain.value?.PharmacyName || '';

      this.pharmacyservice.getActivePharmacyChains(term, this.currentChainPage, 50).subscribe((res: any) => {
        const newChains = res?.ResponseData?.ResponseData ?? [];
        if (newChains.length === 0) {
          this.hasMoreChains = false;
        } else {
          this.chainList = [...this.chainList, ...newChains];
          this.filteredOptions = of(this.chainList);
        }
        this.isLoading = false;
      });
  }

  get errorMessageForPharmacyNcpdpid(): string {
    const transactionform: FormControl = (this.form.get('NCPDPID') as FormControl);
    return transactionform.hasError('maxlength') ?
      'Max length is 7 characters' : 
      transactionform.hasError('pattern') ?
      'Please enter only numeric values':""
  }

  private _filter(value: any): string[] {
    const filterValue = value?.PharmacyName?.toLowerCase() ?? value.toLowerCase();
    return this.chainList?.filter((option: any) =>
      option.PharmacyName.toLowerCase().includes(filterValue)
    ) ?? [];
  }
  
  displayTextForChain(chain: any) {
    return chain ? chain.PharmacyName : '';
  }
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource = this.pharmacyStoreGridCopy.filter((item: PharmacyStoreSearchResponseModel) =>
      Object.values(item).some(val =>
        String(val).toLowerCase().includes(filterValue)
      )
    );
  }
  LoadPharmacyChains(searchTerm: string = '', pageNumber: number = 1, pageSize: number = 500) {
    this.pharmacyservice.getActivePharmacyChains(searchTerm, pageNumber, pageSize).subscribe((res: any) => {
      const newChains = res?.ResponseData?.ResponseData ?? [];
      this.chainList = [...(this.chainList ?? []), ...newChains];
      this.filteredOptions = this.PharmacyChain.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || ''))
      );
    });
  }
  ExportToExcel() {
    this.pharmacyStoreSearchRequestModel.PageNumber = 1;
    this.pharmacyStoreSearchRequestModel.PageSize = this.length;

    this.pharmacyservice.getPharmacyStoreSearchGrid(this.pharmacyStoreSearchRequestModel).subscribe((response: any) => {
      const exportData = response.pharmacyStoreSearchData;

      if (exportData.length > 0) {
        const PharmacyArray = exportData.map((model: any) => ({
          "NCPDP ID": "\t" + model.NCPDPID,
          "NPI": "\t" + (model.NPI ?? ''),
          "Pharmacy Chain Name": model.PharmacyName,
          "Vela PID": "\t" + (model.PID ?? ''),
          "Pharmacy Chain Active": model.IsEnabled,
          "Store Active Date": "\t" + (model.RecordActiveDate ?? ''),
          "Store Deactivate Date": "\t" + (model.RecordDeactivateDate ?? ''),
          "Last Changed Date": "\t" + model.UpdatedDate,
          "Last Changed By": model.UpdatedBy
        }));

        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(PharmacyArray);
        const workbook: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Pharmacy Store');
        XLSX.writeFile(workbook, 'Pharmacy Store.csv');
      } else {
        this.toastr.error("No Data to export");
      }
    }, error => {
      this.toastr.error("Failed to fetch data for export");
    });
  }
  createFormControls() {
    this.PharmacyChain=new FormControl();
    this.NCPDPID = new FormControl('', [Validators.maxLength(7),Validators.pattern("[0-9]+$")]);
  }
  createForm() {
    this.form = new FormGroup({
      PharmacyChain: this.PharmacyChain,
      NCPDPID: this.NCPDPID
    });
  }
  getPharmacyStoreSearchRequestModel() {
    this.pharmacyStoreSearchRequestModel = new PharmacyStoreSearchRequestModel();
    const chain = this.form.get('PharmacyChain')?.value;
    this.pharmacyStoreSearchRequestModel.PharmacyId = chain?.PharmacyId ?? null;
    this.pharmacyStoreSearchRequestModel.NCPDPID = this.form.get('NCPDPID')?.value ?? null;
    this.pharmacyStoreSearchRequestModel.PageNumber = (this.paginator?.pageIndex ?? 0) + 1;
    this.pharmacyStoreSearchRequestModel.PageSize = this.paginator?.pageSize ?? this.pageSizeOptions[0];
  }

  submitForm() {
    if (this.form.valid) {
      this.isLoading = true;
      this.getPharmacyStoreSearchRequestModel();
      this.LoadPharmacyStoresGrid(false);
    }
  }
  private LoadPharmacyStoresGrid(clearTable: boolean) {
    if (clearTable) {
      this.pharmacyStoreGridData = new PharmacyStoreSearchGridData();
      this.dataSource = [];
      this.length = 0;
      this.pharmacyStoreSearchRequestModel.PageNumber = 0;
      this.pharmacyStoreSearchRequestModel.PageSize = this.pageSizeOptions[0];
      return;
    }
    this.isLoading = true;
    this.pharmacyservice.getPharmacyStoreSearchGrid(this.pharmacyStoreSearchRequestModel)
      .subscribe(res => {
        this.pharmacyStoreGridData = res;
        const items = this.pharmacyStoreGridData.pharmacyStoreSearchData ?? [];
        this.dataSource = items;
        this.pharmacyStoreGridCopy = JSON.parse(JSON.stringify(items));
        this.length = this.pharmacyStoreGridData?.pagination?.TotalItems ?? 0;
        this.isLoading = false;
      }, error => {
        this.isLoading = false;
        this.toastr.error(error);
      });
  }
  onPageEvent(event: PageEvent) {
    this.pharmacyStoreSearchRequestModel.PageNumber = event.pageIndex + 1;
    this.pharmacyStoreSearchRequestModel.PageSize = event.pageSize;
    this.LoadPharmacyStoresGrid(false);
  }
  onInputkeyup(event: any) {
    if (event.target.name === 'PharmacyChain') {
      this.form.get('NCPDPID')?.disable();
      const value = this.form.get('PharmacyChain')?.value;
      if (value) {
        const match = this.chainList.find((x: any) =>
          x.PharmacyName.toLowerCase() === value.toLowerCase()
        );
        this.form.get('PharmacyChain')?.setErrors(match ? null : { invalid: true });
      } else {
        this.form.get('PharmacyChain')?.setErrors(null);
      }
    } else if (event.target.name === 'NCPDPID') {
      this.form.get('PharmacyChain')?.disable();
    }
    const input = (event.target as HTMLInputElement).value;
    this.searchInput$.next(input);
  }
  onPharmacyChainChange(event:any)
  {
    this.form.get("NCPDPID")?.disable();
    const selectedChain = event.option.value;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.pharmacyPageNumber = 1;
    this.pharmacyStoreSearchRequestModel.PageNumber = 1;
    this.submitForm();
  }
  clearSearch()
  {
    this.ngOnInit();
    this.LoadPharmacyStoresGrid(true);
  }
}
